
import React, { useMemo } from "react";
import { Tables } from "@/integrations/supabase/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, Clock, MapPin } from "lucide-react";

type CrimeReport = Tables<"crime_reports">;

interface CrimeStatsProps {
  crimeReports: CrimeReport[];
}

const CrimeStats: React.FC<CrimeStatsProps> = ({ crimeReports }) => {
  const stats = useMemo(() => {
    const categories = crimeReports.reduce((acc, report) => {
      acc[report.category] = (acc[report.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const last24Hours = crimeReports.filter(
      (report) => new Date(report.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000)
    ).length;

    const uniqueLocations = new Set(
      crimeReports.map((report) => 
        report.location_name || `${report.latitude.toFixed(4)},${report.longitude.toFixed(4)}`)
    ).size;

    return {
      total: crimeReports.length,
      categories,
      last24Hours,
      uniqueLocations
    };
  }, [crimeReports]);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Total Reports
          </CardTitle>
          <AlertCircle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.total}</div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Last 24 Hours
          </CardTitle>
          <Clock className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.last24Hours}</div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Unique Locations
          </CardTitle>
          <MapPin className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.uniqueLocations}</div>
        </CardContent>
      </Card>
    </>
  );
};

export default CrimeStats;
